import React, { useState } from 'react';
import { Form, Button, Row, Col, InputGroup, Alert, Card, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { buildingTypes, energyClasses, buildingAssetTypes } from '../data/dummyData';
import { theme } from '../theme';

const BuildingForm = ({ initialData: rawInitialData, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  // Guard: default parameter does NOT apply when null is passed explicitly.
  const initialData = rawInitialData || {};

  // Extract building metadata from the API response (has_rooftop_pv etc. are stored in metadata JSONB)
  const initMeta = initialData.metadata || {};

  // Hydrate asset specs from building_assets array (API response shape)
  // The API returns: building_assets: [{ asset_type: 'heat_pump', specs: {...} }, ...]
  // The form expects: heat_pump: {...specs}, has_heat_pump: true
  const initAssets = {};
  if (Array.isArray(initialData.building_assets)) {
    for (const a of initialData.building_assets) {
      const key = a.asset_type;
      if (key && buildingAssetTypes[key]) {
        initAssets[`has_${key}`] = true;
        initAssets[key] = a.specs || {};
      }
    }
  }

  // Build initial form state safely. We do NOT spread initialData at the end
  // because the API response may contain fields like building_assets, datasets, etc.
  // that would pollute the form state, and asset keys could be null which would
  // break property access later.
  const [formData, setFormData] = useState({
    // Core building fields
    building_id: initialData.building_id || '',
    building_type: initialData.building_type || 'residential',
    floor_area: initialData.floor_area_m2 || initialData.floor_area || '',
    energy_class: initialData.energy_class || 'C',
    // Building asset flags — prefer hydrated from building_assets array, fall back to metadata flags
    has_rooftop_pv: initAssets.has_rooftop_pv || initMeta.has_rooftop_pv || initialData.has_rooftop_pv || false,
    rooftop_pv: initAssets.rooftop_pv || initialData.rooftop_pv || {},
    has_battery: initAssets.has_battery || initMeta.has_battery || initialData.has_battery || false,
    battery: initAssets.battery || initialData.battery || {},
    has_heat_pump: initAssets.has_heat_pump || initMeta.has_heat_pump || initialData.has_heat_pump || false,
    heat_pump: initAssets.heat_pump || initialData.heat_pump || {},
    has_hvac: initAssets.has_hvac || initMeta.has_hvac || initialData.has_hvac || false,
    hvac: initAssets.hvac || initialData.hvac || {},
    has_ev_charger: initAssets.has_ev_charger || initMeta.has_ev_charger || initialData.has_ev_charger || false,
    ev_charger: initAssets.ev_charger || initialData.ev_charger || {},
    // Preserve the internal ID for edit detection
    id: initialData.id || null,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAssetCheckboxChange = (assetKey, checked) => {
    setFormData(prev => ({
      ...prev,
      [`has_${assetKey}`]: checked,
      // Use empty object instead of null to prevent null-safety issues
      [assetKey]: checked ? (prev[assetKey] || {}) : {},
    }));
  };

  const handleAssetFieldChange = (assetKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      [assetKey]: {
        ...(prev[assetKey] || {}),
        [field]: value,
      },
    }));
    // Clear error for this field
    if (errors[`${assetKey}.${field}`]) {
      setErrors(prev => ({ ...prev, [`${assetKey}.${field}`]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate required building fields
    if (!formData.building_id || formData.building_id.trim() === '') {
      newErrors.building_id = t('building_form.validation_id_required');
    }

    if (!formData.building_type) {
      newErrors.building_type = t('building_form.validation_id_required');
    }

    if (!formData.floor_area || formData.floor_area <= 0) {
      newErrors.floor_area = t('building_form.validation_area_positive');
    }

    // Validate building assets
    const assetValidations = {
      rooftop_pv: formData.has_rooftop_pv,
      battery: formData.has_battery,
      heat_pump: formData.has_heat_pump,
      hvac: formData.has_hvac,
      ev_charger: formData.has_ev_charger,
    };

    Object.entries(assetValidations).forEach(([assetKey, isEnabled]) => {
      if (isEnabled) {
        const assetType = buildingAssetTypes[assetKey];
        if (assetType) {
          assetType.fields.forEach(field => {
            if (field.required && !formData[assetKey]?.[field.name]) {
              newErrors[`${assetKey}.${field.name}`] = `${field.label} is required`;
            }
          });
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const renderAssetFields = (assetKey, assetType) => {
    if (!assetType || !assetType.fields) return null;
    return (
      <Collapse in={formData[`has_${assetKey}`]}>
        <div>
          <Card
            className="mt-3 mb-3"
            style={{
              borderLeft: `4px solid ${theme.colors.primary.main}`,
              backgroundColor: theme.colors.primary.pale,
            }}
          >
            <Card.Body>
              <h6 className="mb-3">
                <span className="me-2">{assetType.icon || ''}</span>
                {assetType.label || assetKey} Specifications
              </h6>
              <Row>
                {assetType.fields.map((field, idx) => {
                  const value = formData[assetKey]?.[field.name] ?? field.default ?? '';
                  const error = errors[`${assetKey}.${field.name}`];

                  return (
                    <Col md={6} key={field.name}>
                      {field.type === 'select' ? (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {field.label} {field.required && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Select
                            value={value}
                            onChange={(e) => handleAssetFieldChange(assetKey, field.name, e.target.value)}
                            isInvalid={!!error}
                          >
                            <option value="">Select {field.label}</option>
                            {field.options.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {error}
                          </Form.Control.Feedback>
                        </Form.Group>
                      ) : (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {field.label} {field.required && <span className="text-danger">*</span>}
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              value={value}
                              onChange={(e) => handleAssetFieldChange(assetKey, field.name, parseFloat(e.target.value) || '')}
                              isInvalid={!!error}
                              step="any"
                            />
                            {field.unit && <InputGroup.Text>{field.unit}</InputGroup.Text>}
                            <Form.Control.Feedback type="invalid">
                              {error}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>
                      )}
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        </div>
      </Collapse>
    );
  };

  const buildingTypeObj = buildingTypes.find(t => t.value === formData.building_type);

  return (
    <Form onSubmit={handleSubmit}>
      <div className="mb-4 p-3 rounded" style={{ backgroundColor: theme.colors.neutral[50] }}>
        <h5>
          <span className="me-2">🏢</span>
          {t('building_form.section_basic')}
        </h5>
      </div>

      {/* Building ID */}
      <Form.Group className="mb-3">
        <Form.Label>
          {t('building_form.field_building_id')} <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={t('building_form.field_building_id_placeholder')}
          value={formData.building_id}
          onChange={(e) => handleChange('building_id', e.target.value)}
          isInvalid={!!errors.building_id}
        />
        <Form.Control.Feedback type="invalid">
          {errors.building_id}
        </Form.Control.Feedback>
        <Form.Text className="text-muted">
          {t('building_form.field_building_id_hint')}
        </Form.Text>
      </Form.Group>

      <Row>
        {/* Building Type */}
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('building_form.field_building_type')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={formData.building_type}
              onChange={(e) => handleChange('building_type', e.target.value)}
              isInvalid={!!errors.building_type}
            >
              {buildingTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.building_type}
            </Form.Control.Feedback>
            {buildingTypeObj && (
              <Form.Text className="text-muted">
                {buildingTypeObj.description}
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        {/* Floor Area */}
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('building_form.field_floor_area')} <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                value={formData.floor_area}
                onChange={(e) => handleChange('floor_area', parseFloat(e.target.value))}
                isInvalid={!!errors.floor_area}
              />
              <InputGroup.Text>m²</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.floor_area}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        {/* Energy Class */}
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('building_form.field_energy_class')}</Form.Label>
            <Form.Select
              value={formData.energy_class}
              onChange={(e) => handleChange('energy_class', e.target.value)}
            >
              {energyClasses.map(cls => (
                <option key={cls} value={cls}>
                  {cls}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Building Assets Section */}
      <div className="mt-4 mb-3 p-3 rounded" style={{ backgroundColor: theme.colors.neutral[50] }}>
        <h5>
          <span className="me-2">⚙️</span>
          {t('building_form.section_assets')}
        </h5>
      </div>

      {/* Rooftop PV */}
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          label={
            <span>
              <span className="me-2">{buildingAssetTypes.rooftop_pv.icon}</span>
              <strong>{t('building_form.has_rooftop_pv')}</strong>
            </span>
          }
          checked={formData.has_rooftop_pv}
          onChange={(e) => handleAssetCheckboxChange('rooftop_pv', e.target.checked)}
        />
      </Form.Group>
      {renderAssetFields('rooftop_pv', buildingAssetTypes.rooftop_pv)}

      {/* Battery Storage */}
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          label={
            <span>
              <span className="me-2">{buildingAssetTypes.battery.icon}</span>
              <strong>{t('building_form.has_battery')}</strong>
            </span>
          }
          checked={formData.has_battery}
          onChange={(e) => handleAssetCheckboxChange('battery', e.target.checked)}
        />
      </Form.Group>
      {renderAssetFields('battery', buildingAssetTypes.battery)}

      {/* Heat Pump */}
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          label={
            <span>
              <span className="me-2">{buildingAssetTypes.heat_pump.icon}</span>
              <strong>{t('building_form.has_heat_pump')}</strong>
            </span>
          }
          checked={formData.has_heat_pump}
          onChange={(e) => handleAssetCheckboxChange('heat_pump', e.target.checked)}
        />
      </Form.Group>
      {renderAssetFields('heat_pump', buildingAssetTypes.heat_pump)}

      {/* HVAC */}
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          label={
            <span>
              <span className="me-2">{buildingAssetTypes.hvac.icon}</span>
              <strong>{t('building_form.has_hvac')}</strong>
            </span>
          }
          checked={formData.has_hvac}
          onChange={(e) => handleAssetCheckboxChange('hvac', e.target.checked)}
        />
      </Form.Group>
      {renderAssetFields('hvac', buildingAssetTypes.hvac)}

      {/* EV Charger */}
      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          label={
            <span>
              <span className="me-2">{buildingAssetTypes.ev_charger.icon}</span>
              <strong>{t('building_form.has_ev_charger')}</strong>
            </span>
          }
          checked={formData.has_ev_charger}
          onChange={(e) => handleAssetCheckboxChange('ev_charger', e.target.checked)}
        />
      </Form.Group>
      {renderAssetFields('ev_charger', buildingAssetTypes.ev_charger)}

      <div className="d-flex gap-2 justify-content-end mt-4">
        <Button variant="outline-secondary" onClick={onCancel}>
          {t('building_form.btn_cancel')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          style={{ backgroundColor: theme.colors.primary.main }}
        >
          {initialData.id ? t('buildings_setup.modal_edit') : t('buildings_setup.modal_add')}
        </Button>
      </div>
    </Form>
  );
};

export default BuildingForm;
